/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useState, useEffect } from "react";
import Placeholder from "../../assets/AAACard.png";
import Picker from "emoji-picker-react";
import Button from "../Shared/Button";
import { CirclePlus } from "lucide-react";
import Input from "../Shared/Input";
import TextArea from "../Shared/TextArea";
import Selectbox from "../Shared/SelectBox";
import ArrowIcon from "../Icons/ArrowIcon";
import MultipleUpload from "../Shared/MultipleUpload";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AddNewTicket } from "../../api/Support/AddTicket";
import { ButtonTypes } from "../../utils/consts";
import { GetStudentTickets } from "../../api/Support/GetStudentTickets";
import { ReplyTicket } from "../../api/Support/ReplyTicket";
import { UpdateTicket } from "../../api/Support/UpdateStatus";
import { TicketDTO } from "../../api/Support/GetStudentTickets";
import Spinner from "../Shared/Spinner";
import { GetTicketById } from "../../api/Support/GetTicketById";
import { queryClient } from "../../main";
import { toast } from "react-toastify";
import { getAccessToken } from "../../lib/session";
const validationSchema = Yup.object({
    subject: Yup.string().required("Ticket subject is required"),
    message: Yup.string().required("Message is required"),
});

interface Option {
    id?: string;
    name: string;
}

const options = [
    { name: 'Technical Issues' },
    { name: 'Financial Issues' },
    { name: 'Course Problem' },
    { name: 'Refund' },
];

const Support = () => {
    const isLoggedIn = !!getAccessToken();
    const [inputStr, setInputStr] = useState("");
    const [showPicker, setShowPicker] = useState(false);
    const [selected, setSelected] = useState<Option | null>(null);
    const [showForm, setShowForm] = useState(!isLoggedIn);
    const [selectedTicket, setSelectedTicket] = useState<TicketDTO | null>(null);


    const onEmojiClick = (emojiObject: { emoji: string }) => {
        setInputStr((prevInput) => prevInput + emojiObject.emoji);
        setShowPicker(false);
    };

    const { mutate } = useMutation({
        mutationFn: AddNewTicket,
        onSuccess: () => {
            formik.resetForm();
            queryClient.invalidateQueries({ queryKey: ['ticket'] });
        },
        onError: () => {
            formik.setSubmitting(false);
        },
    });

    const { mutate: replyTicket } = useMutation({
        mutationFn: ({ message, attachments }: { message: string; attachments: string[] }) => {
            if (selectedTicket) {
                return ReplyTicket({ message, attachments }, selectedTicket.id);
            }
            return Promise.reject("No ticket selected");
        },
        onSuccess: (_data, variables) => {
            if (selectedTicket) {
                const updatedTicket = {
                    ...selectedTicket,
                    supportMessages: [
                        ...selectedTicket.supportMessages,
                        {
                            id: Date.now().toString(),
                            message: variables.message,
                            attachments: variables.attachments,
                            createdAt: new Date().toISOString(),
                            adminId: null,
                            studentId: selectedTicket.studentId,
                        },
                    ],
                    updatedAt: new Date().toISOString(),
                };
                setSelectedTicket(updatedTicket);
            }

            queryClient.invalidateQueries({ queryKey: ['ticket'] });
            setInputStr("");
        },
        onError: (error) => {
            console.error("Failed to reply to ticket:", error);
        },
    });

    const { mutate: updateTicketStatus } = useMutation({
        mutationFn: (ticketId: string) => {
            return UpdateTicket({ status: "Resolved" }, ticketId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['ticket'] });
            if (selectedTicket) {
                setSelectedTicket({ ...selectedTicket, status: "Resolved" });
            }
        },
        onError: (error) => {
            console.error("Failed to update ticket status:", error);
        },
    });

    const formik = useFormik({
        initialValues: {
            subject: "",
            categoryName: "",
            message: "",
            attachments: [],
        },
        validationSchema,
        onSubmit: (values) => {
            if (!isLoggedIn) {
                toast.error("Please sign in first to submit a ticket.");
                return;
            }
            const payload = {
                subject: values.subject,
                categoryName: selected ? selected.name : "",
                supportMessages: [
                    {
                        message: values.message,
                        attachments: values.attachments,
                    },
                ],
            };
            //@ts-expect-error
            mutate(payload);
        },
    });

    const { data: TicketResponse, isLoading: isTicketLoading } = useQuery({
        queryFn: GetStudentTickets,
        queryKey: ["ticket"],
        enabled: !!getAccessToken(),
        staleTime: 0,
        refetchInterval: 0,
    });

    const Tickets = TicketResponse?.data?.TicketData || [];

    useEffect(() => {
        if (Tickets.length > 0 && !selectedTicket) {
            setSelectedTicket(Tickets[0]);
        }
    }, [Tickets, selectedTicket]);

    useEffect(() => {
        if (selectedTicket) {
            queryClient.invalidateQueries({ queryKey: ["ticket", selectedTicket.id] });
        }
    }, [selectedTicket, queryClient]);

    const { data: TicketChatResponse, isLoading: isChatLoading } = useQuery({
        queryFn: () => GetTicketById(selectedTicket?.id || ""),
        queryKey: ["ticket", selectedTicket?.id],
        enabled: !!selectedTicket?.id,
        staleTime: 0,
        refetchInterval: 0,
    });

    const ticketChat = TicketChatResponse?.data?.TicketData[0];
    const handleFileUpload = (url: string) => {
        formik.setFieldValue("attachments", [
            ...formik.values.attachments,
            url,
        ]);
    };

    const handleReplySubmit = () => {
        if (inputStr.trim()) {
            replyTicket({ message: inputStr, attachments: [] });
        }
    };

    const handleTicketClick = (ticket: TicketDTO) => {
        setSelectedTicket(ticket);
    };

    const handleMarkAsResolved = () => {
        if (selectedTicket) {
            updateTicketStatus(selectedTicket.id);
        }
    };
    const countAdminMessagesBeforeStudent = (ticket: TicketDTO) => {
        let adminMessageCount = 0;

        const reversedMessages = [...ticket.supportMessages].reverse();

        for (const message of reversedMessages) {
            if (message.adminId === null) {
                break;
            }
            if (message.adminId !== null) {
                adminMessageCount++;
            }
        }

        return adminMessageCount;
    };


    return (
        <div className="bg-[#fff] pb-12 pt-10">
            <div className="w-full container h-full flex items-center justify-center pt-20">
                <div className="border border-black/15 rounded-md w-full flex min-h-[900px] pt-7 bg-[#F3F5F6]">
                    {isTicketLoading ? (<div className="flex items-center justify-center w-1/3">
                        <Spinner />
                    </div>)
                        : (
                            <div className={`hidden lg:block w-1/3${!isLoggedIn ? " !hidden" : ""}`}>
                                <div className="px-5">
                                    <button
                                        onClick={() => setShowForm(true)}
                                        className="w-full transition-all duration-300 flex flex-col items-center py-2 border text-primary hover:bg-primary hover:text-white rounded-lg mb-4"
                                    >
                                        <CirclePlus className="size-10" />
                                        <h1 className="text-[26px] font-bold">
                                            Submit a new Ticket!
                                        </h1>
                                    </button>
                                    <h1 className="text-xl font-bold mb-3">
                                        Recent Tickets
                                    </h1>
                                </div>
                                <div className="flex-1 h-full overflow-x-hidden overflow-y-auto max-h-[700px] px-5">
                                    {Tickets?.map((ticket) => {
                                        const updatedDate = ticket.updatedAt ? new Date(
                                            ticket.updatedAt
                                        ).toLocaleDateString("en-GB", {
                                            day: "numeric",
                                            month: "long",
                                        }) : 'Date not available';
                                        const latestMessage =
                                            ticket.supportMessages.length > 0
                                                ? ticket.supportMessages[
                                                    ticket.supportMessages.length - 1
                                                ].message
                                                : "No messages yet";
                                                const adminMessageCount = ticket.status === 'Awaiting_student_reply' ? countAdminMessagesBeforeStudent(ticket) : 0;

                                        return (
                                            <div
                                                key={ticket.id}
                                                onClick={() =>
                                                    handleTicketClick(ticket)
                                                }
                                                className="entry cursor-pointer transform hover:scale-105 duration-300 transition-transform bg-white mb-4 rounded p-4 flex shadow-md border"
                                            >
                                                <div className="">
                                                    <div className="w-12 h-12 relative">
                                                        <img
                                                            className="w-12 h-12 rounded-full mx-auto"
                                                            src={Placeholder}
                                                            alt="chat-user"
                                                        />
                                                        <span className="absolute w-4 h-4 bg-green-400 rounded-full right-0 bottom-0 border-2 border-white"></span>
                                                    </div>
                                                </div>
                                                <div className="flex-1 px-2">
                                                    <div className="truncate w-32">
                                                        <span className="text-gray-800 capitalize">
                                                            {ticket.assignedTo ? (ticket?.assignedTo?.firstName + ' ' + ticket?.assignedTo?.lastName
                                                            ) : ('AAA Support')}

                                                        </span>
                                                    </div>
                                                    <div>
                                                        <small className="text-gray-600">
                                                            {latestMessage}
                                                        </small>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div>
                                                        <small className="text-gray-500">
                                                            {updatedDate}
                                                        </small>
                                                    </div>
                                                    <div>
                                                        {adminMessageCount !== 0 &&
                                                        <small className="text-xs bg-red-500 text-white rounded-full h-6 w-6 leading-6 text-center inline-block">
                                                        {adminMessageCount}
                                                        </small>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    {!showForm ? (
                        isChatLoading ? (<div className="flex items-center justify-center w-2/3">
                            <Spinner />
                        </div>) : (
                            <div className="sm:px-6 px-3 flex-1 flex flex-col">
                                <div className="flex justify-between gap-7 sm:items-center sm:flex-row flex-col mb-5 rounded-lg border p-4">
                                    <div>
                                        <h1 className="font-bold text-2xl">
                                            Ticket #{selectedTicket?.id?.slice(0, 7) || 'N/A'}
                                        </h1>
                                        <h2 className="font-medium text-lg">
                                            Status:{" "}
                                            <span className={`font-bold ${selectedTicket?.status === 'Resolved' ? 'text-green-500' : 'text-red-500'}`}>
                                                {selectedTicket?.status === 'Resolved' ? 'Resolved' : 'Open'}
                                            </span>
                                        </h2>
                                    </div>
                                    <Button
                                        variant="transparent"
                                        className="before:!bg-primary hover:text-white !text-base !font-bold !h-11 sm:w-40 w-full !border-primary !border-2 text-primary"
                                        onClick={handleMarkAsResolved}
                                    >
                                        Mark As Resolved
                                    </Button>
                                </div>
                                <div className="messages flex-1 overflow-auto max-h-[600px]">
                                    {ticketChat && ticketChat.supportMessages && ticketChat.supportMessages.length > 0 ? (
                                        ticketChat.supportMessages.map((message, index) => (
                                            <div
                                                key={index}
                                                className={
                                                    message.adminId === null
                                                        ? "message me mb-4 flex text-right"
                                                        : "message mb-4 flex"
                                                }
                                            >
                                                {message.studentId === null ? (
                                                    <div className="">
                                                        <div className="w-12 h-12 relative">
                                                            <img
                                                                className="w-12 h-12 rounded-full mx-auto"
                                                                src={Placeholder}
                                                                alt="chat-user"
                                                            />
                                                            <span className="absolute w-4 h-4 bg-green-500 rounded-full right-0 bottom-0 border-2 border-white"></span>
                                                        </div>
                                                    </div>
                                                ) : null}
                                                <div className="flex-1 px-2">
                                                    <div
                                                        className={`inline-block ${message.adminId === null
                                                            ? "bg-blue-600 text-white"
                                                            : "bg-gray-300 text-gray-700"
                                                            } rounded-full p-2 px-6`}
                                                    >
                                                        <span>
                                                            {message.message || "No message content"}
                                                        </span>
                                                    </div>
                                                    <div
                                                        className={
                                                            message.adminId === null
                                                                ? "pr-4"
                                                                : "pl-4"
                                                        }
                                                    >
                                                        <small className="text-gray-500">
                                                            {message.createdAt ? new Date(
                                                                message.createdAt
                                                            ).toLocaleDateString() : 'Date not available'}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p>No messages found for this ticket.</p>
                                    )}
                                </div>
                                <div className="pt-4 pb-10">
                                    <div className={`write shadow flex border rounded-lg ${selectedTicket?.status === 'Resolved' ? 'pointer-events-none cursor-not-allowed bg-gray-50' : 'bg-white'}`}>
                                        <div className="flex-3 flex content-center items-center text-center p-4 pr-0 relative">
                                            <span
                                                onClick={() =>
                                                    setShowPicker((val) => !val)
                                                }
                                                className="block text-center text-gray-400 hover:text-gray-800"
                                            >
                                                <svg
                                                    fill="none"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    className="h-6 w-6"
                                                >
                                                    <path d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                                </svg>
                                            </span>
                                            {showPicker && (
                                                <div className="absolute bottom-14">
                                                    <Picker
                                                        onEmojiClick={
                                                            onEmojiClick
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <input
                                                value={inputStr}
                                                onChange={(e) =>
                                                    setInputStr(e.target.value)
                                                }
                                                name="message"
                                                disabled={selectedTicket?.status === 'Resolved'}
                                                className={`w-full block outline-none py-4 px-4 bg-transparent`}

                                                placeholder={`${selectedTicket?.status === 'Resolved' ? 'You cant reply to this conversation , its already solved' : 'Type a message...'}`}
                                            ></input>
                                        </div>
                                        <div className="w-32 p-2 flex content-center items-center">
                                            <div className="flex-1 text-center">
                                                <span className="text-gray-400 hover:text-gray-800">
                                                    <span className="inline-block align-text-bottom">
                                                        <svg
                                                            fill="none"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                            className="w-6 h-6"
                                                        >
                                                            <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
                                                        </svg>
                                                    </span>
                                                </span>
                                            </div>
                                            <div className="flex-1">
                                                <button onClick={handleReplySubmit} className="bg-blue-400 w-10 h-10 rounded-full inline-block">
                                                    <span className="inline-block align-text-bottom">
                                                        <svg
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            viewBox="0 0 24 24"
                                                            className="w-4 h-4 text-white"
                                                        >
                                                            <path d="M5 13l4 4L19 7"></path>
                                                        </svg>
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        )
                    ) : (
                        <form
                            onSubmit={formik.handleSubmit}
                            className="sm:px-6 px-3 flex-1 flex flex-col"
                        >
                            {isLoggedIn && (
                            <button
                                onClick={() => setShowForm(false)}
                            >
                                <ArrowIcon />
                            </button>
                            )}
                            <h1 className="text-3xl font-bold border-b pb-5 mb-5">
                                Please fill the fields below.
                            </h1>
                            <div className="space-y-3">
                                <Input
                                    value={formik.values.subject}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.subject &&
                                            formik.errors.subject
                                            ? formik.errors.subject
                                            : ""
                                    }
                                    title="Ticket Subject:"
                                    inputStyles="!h-12"
                                    name="subject"
                                />
                                <Selectbox
                                    title="Select Category:"
                                    titleStyles="!text-base !mb-2 !font-normal"
                                    className="!h-12 bg-white"
                                    name="categoryName"
                                    selectOptions={options}
                                    setSelected={setSelected}
                                    selected={selected}
                                />
                                <TextArea
                                    value={formik.values.message}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.message &&
                                            formik.errors.message
                                            ? formik.errors.message
                                            : ""
                                    }
                                    placeholder=""
                                    title="Your Question:"
                                    name="message"
                                    inputStyles="h-[200px]"
                                />
                                <MultipleUpload
                                    folder="tickets"
                                    questionId={0}
                                    onFileUploaded={handleFileUpload}
                                >
                                    <h1 className="md:text-base text-sm sm:mb-2 mb-1.5">
                                        Upload Attachment:
                                    </h1>
                                    <div className="size-[200px] border-[3px] text-[#e5e7eb] hover:border-primary hover:text-primary transition-all duration-300 rounded-lg border-dashed flex justify-center items-center">
                                        <CirclePlus className="size-14" />
                                    </div>
                                </MultipleUpload>
                                <Button
                                    type={ButtonTypes.SUBMIT}
                                    disabled={formik.isSubmitting}
                                    variant="transparent"
                                    className="before:!bg-primary hover:text-white !text-base !font-bold !h-11 w-40 !border-primary !border-2 text-primary ml-auto !mb-7"
                                >
                                    Submit Ticket
                                </Button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Support;



